import axios from 'axios'
import axiosRetry from 'axios-retry'
import { fromUnixTime, isBefore, subSeconds } from 'date-fns'
import FileSaver from 'file-saver'
import { Capacitor } from '@capacitor/core'
import { t } from 'translations'

import { renewToken, setLoggedOut } from 'store/actions/auth'
import { store } from 'store/reducers'

let _API_PATH = ''
if (process.env.REACT_APP_API_ENDPOINT) _API_PATH = process.env.REACT_APP_API_ENDPOINT
else if (process.env.NODE_ENV === 'production') _API_PATH = '/api/'
else _API_PATH = `http://${window.location.hostname}:3000/api/`

export const API_PATH = _API_PATH
// ------- Auth API -------
export const Auth = axios.create({ baseURL: API_PATH + 'auth' })
Auth.interceptors.response.use(
  res => (res.data ? res.data : res),
  err => {
    if (!err.response) return { success: false, message: t('auth.NO_INTERNET') }
    if (!err.response.status) return { success: false }
    return err.response.data || { success: false } // Return false if it is an 5XX error not from our API
  }
)
axiosRetry(Auth, { retries: 2, retryDelay: () => 5000 })

// ------- Open API -------
export const OpenAPI = axios.create({ baseURL: API_PATH + 'open' })
OpenAPI.interceptors.response.use(
  res => (res.data ? res.data : res),
  err => {
    if (!err.response) return { success: false, message: t('auth.NO_INTERNET') }
    if (!err.response.status) return { success: false }
    return err.response.data || { success: false } // Return false if it is an 5XX error not from our API
  }
)

axiosRetry(OpenAPI, { retries: 2, retryDelay: () => 5000 })

const Api = axios.create({ baseURL: API_PATH })

axiosRetry(Api, { retries: 2, retryDelay: () => 5000 })

// Transform response to send only the data
Api.interceptors.response.use(
  res => {
    if (process.env.IS_TEST) return res.data
    if (!res.headers) return res
    // a Content-Disposition header with the `attachment` value indicates that the content should be downloaded and saved locally.
    // Therefore, if a responses contains this header, prompt a file download with the content of the body.
    // https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Content-Disposition
    if (res.headers && res.headers['content-disposition'] && res.headers['content-disposition'].includes('attachment')) {
      // Lookup the filename in the Content-Disposition headers.
      // If the filename is not present, use `download` as the default filename
      const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/g
      const filename = res.headers['content-disposition'].match(regex) || ['filename=download']
      const blob = new Blob([res.data], { type: res.headers['content-type'] })
      FileSaver.saveAs(blob, filename[0].split('=')[1])
    }
    return res.data
  },
  async err => {
    // Handles the 401 error from the backend
    if (err.response && err.response.status === 401) {
      // Logout the user
      await setLoggedOut(store)
      // Reload to reset the app and the component waiting for the request to end
      window.location.reload()
      // Makes sure the app has time to reload before handing the failed request
      return new Promise(resolve => setTimeout(resolve, 15000))
    }
    if (!err.response) return { success: false, message: t('auth.NO_INTERNET') }
    if (!err.response.status) return { success: false }
    return err.response.data || { success: false } // Return false if it is an 5XX error not from our API
  }
)
Api.defaults.headers.common['token'] = localStorage.getItem('token')

// Validate the token (if not expired), if it is expired refresh it.
Api.interceptors.request.use(async req => {
  // Disable for testing
  if (process.env.IS_TEST) return req

  // If getting a renew token is in process, await for it
  const { isRenewingToken, newTokenPromise } = store.getState().auth

  // Await the previous request made and use the return token
  if (isRenewingToken) {
    req.headers.token = await newTokenPromise
    return req
  }

  // Fetch the token into the store
  const { decodedToken } = store.getState().auth
  // Set the platform type header
  const { platform } = store.getState().applicationInfo
  req.headers.merinioUsedPlatform = platform

  const tokenExpirationWithTimeRemoved = subSeconds(fromUnixTime(parseInt(decodedToken.exp)), 30)
  const isExpiringSoon = isBefore(tokenExpirationWithTimeRemoved, new Date())

  if (!isExpiringSoon) return req

  req.headers.token = await renewToken(store)
  return req
})

if (Capacitor.isNativePlatform()) {
  // Tell the API that this is a request made by the mobile app
  Api.defaults.headers.common['mobile'] = true
  Auth.defaults.headers.common['mobile'] = true
  OpenAPI.defaults.headers.common['mobile'] = true
}

export default Api
